import { wasmModule } from "../wasm/wasmModule"

import { resourceManager, blobToFile } from "./resourcemgr"
import JSZip from "jszip"
import {
  FS_BASEPATH,
  FS_MKDIR,
  SCANS_PHOTO,
  SCREEN_SHOT,
  FS_FILEZIP,
  FS_RESOURCE_TO_CASE,
  FS_RESOURCE_TO_CASEFILE,
} from "./common"
import { CaseZipType, ZipFileName, ZipFileList } from "../../common"
import {
  CASE_ZIP_FOLDERS,
  getZipBaseNames,
  isValidCaseRef,
  toZipBaseName,
} from "../../common/zipFileNames"

/**
 * Zips rebuilt from the virtual FS that would lose server-side content if their
 * `Setting Data*` folder was never loaded in this session.
 */
const OVERWRITE_GUARDED_ZIPS = ["bulk0", "bulk1", "bulk2", "bulk10"]

/**
 * - "check": report guarded zips the server has but this session never loaded,
 *   and drop them when zipGuardAction is "drop" (default when names are given)
 * - "allowRebuild": the caller rebuilds the whole case on purpose (default when no names are given)
 * - "skip": local export only, nothing is uploaded
 */
export type ZipOverwriteGuardMode = "check" | "allowRebuild" | "skip"

export interface SaveAndGetFileZipsOptions {
  guard?: ZipOverwriteGuardMode
  /**
   * the caller knows the case already exists on the server (e.g. an Attention
   * Needed form): an unknown server list is never treated as a new case
   */
  isExistingCase?: boolean
  /**
   * case the zips are built for: its server list is used even when the owner
   * of the virtual FS is unknown. Without it, only the owner's list is trusted.
   */
  caseId?: string
}

export type ZipGuardAction = "log" | "drop"

/**
 * After a failed static resource reload (17 MB resource.zip), saves don't
 * retry it before this delay: each save would otherwise wait for it again.
 */
const STATIC_RESOURCE_RETRY_DELAY_MS = 5 * 60 * 1000

/** called when the virtual FS starts holding the files of another case */
export type CaseClaimedListener = (caseId: string) => void

export type ZipGuardReporter = (
  eventName: string,
  payload: Record<string, unknown>,
) => void

class ResourcesSynchronization {
  ismkdirdone = false
  isnewhismkdirdone = false
  isoldhismkdirdone = false
  /** zip names (without .zip) loaded into the case virtual FS in this session */
  private _hydratedZips = new Set<string>()
  /**
   * case owning the /test/case content, null when unknown (emptied FS, or
   * loaded by a path that doesn't pass a caseId)
   */
  private _activeCaseId: string | null = null
  /** zip names known on the server for _serverZipsCaseId, null when unknown */
  private _serverZips: Set<string> | null = null
  private _serverZipsCaseId: string | null = null
  private _zipGuardReporter: ZipGuardReporter | null = null
  private _caseClaimedListener: CaseClaimedListener | null = null
  /** reload in progress, shared by concurrent saves */
  private _staticReload: Promise<Record<string, File>> | null = null
  /** Date.now() of the last failed reload, 0 when none */
  private _staticReloadFailedAt = 0
  /**
   * "log" until RUM confirms which flows trigger the guard, then switch to "drop"
   * to stop rebuilding unloaded zips.
   */
  zipGuardAction: ZipGuardAction = "log"

  setZipGuardReporter(reporter: ZipGuardReporter | null) {
    this._zipGuardReporter = reporter
  }

  /** e.g. to drop the data cached for the other cases */
  setCaseClaimedListener(listener: CaseClaimedListener | null) {
    this._caseClaimedListener = listener
  }

  private _setActiveCase(caseId: string) {
    const isNewOwner = this._activeCaseId !== caseId
    this._activeCaseId = caseId
    if (!isNewOwner) return
    try {
      this._caseClaimedListener?.(caseId)
    } catch (err) {
      console.warn("[zip-guard] case claimed listener failed:", err)
    }
  }

  /**
   * Reports a zip event (setZipGuardReporter), e.g. from the zip loaders.
   * A reporting failure never breaks the caller's flow.
   */
  report(eventName: string, payload: Record<string, unknown>) {
    try {
      this._zipGuardReporter?.(eventName, payload)
    } catch (err) {
      console.warn("[zip-guard] reporter failed:", err)
    }
  }

  /**
   * @param fileNames server file names of the case (e.g. "bulk2.zip", "RetouchHistory.json")
   */
  setServerCaseFiles(caseId: string, fileNames: string[]) {
    if (!isValidCaseRef(caseId) || !Array.isArray(fileNames)) return
    this._serverZipsCaseId = caseId
    this._serverZips = new Set(getZipBaseNames(fileNames))
  }

  /**
   * server list of caseId (the virtual FS owner by default), null when unknown:
   * a list fetched for another case says nothing about this one
   */
  private _getServerZips(caseId?: string): Set<string> | null {
    const targetCaseId = caseId ?? this._activeCaseId
    return targetCaseId !== null && this._serverZipsCaseId === targetCaseId
      ? this._serverZips
      : null
  }

  resetHydration() {
    this._hydratedZips.clear()
  }

  /**
   * the case is closed but its files may stay in the virtual FS: the next
   * ensureCaseZipsLoaded loads the server zips again, only filling the gaps.
   * Nothing is emptied: closeCase also runs before work that writes into the
   * virtual FS (presetup, preview, order flow), whose files would be lost.
   */
  markCaseClosed() {
    this.resetHydration()
  }

  getActiveCaseId(): string | null {
    return this._activeCaseId
  }

  /**
   * @param caseId when given, zips loaded for another case count as not loaded
   */
  getHydratedZips(caseId?: string): string[] {
    if (
      caseId &&
      this._activeCaseId !== null &&
      this._activeCaseId !== caseId
    ) {
      return []
    }
    return [...this._hydratedZips]
  }

  /**
   * Records caseId as the owner of the virtual FS right after it was emptied,
   * for flows that write into it without loading a zip (e.g. a scan dropped in
   * the Preview): their files are then discarded when another case is loaded,
   * and kept as local writes for caseId.
   * Only call it on an empty virtual FS.
   */
  claimEmptyVirtualFS(caseId: string) {
    if (!isValidCaseRef(caseId)) return
    this._setActiveCase(caseId)
  }

  /**
   * Empties the case virtual FS when it belongs to another case, even when
   * nothing will be loaded (e.g. the server doesn't have the zips yet).
   * @returns true when the virtual FS was emptied
   */
  async discardOtherCase(caseId: string): Promise<boolean> {
    if (!isValidCaseRef(caseId)) return false
    if (this._activeCaseId === null || this._activeCaseId === caseId) {
      return false
    }
    return this._claimVirtualFS(caseId)
  }

  /**
   * Makes sure the case virtual FS only holds files of caseId before loading
   * its zips: it's shared by every case of the tab (the WASM module is created
   * once) and not always emptied when a case is closed.
   * Empties it when it belongs to another case. Otherwise, files of an unknown
   * owner were loaded or written in this flow (local writes win).
   * @returns true when the virtual FS was emptied
   */
  private async _claimVirtualFS(caseId: string): Promise<boolean> {
    if (this._activeCaseId === caseId) return false

    const isOtherCase = this._activeCaseId !== null
    if (isOtherCase) {
      const payload = {
        previousCaseId: this._activeCaseId,
        caseId,
        reason: "otherCase",
      }
      console.warn(
        "[zip-guard] emptying the case virtual FS before loading case zips:",
        payload,
      )
      // files written since the last load are lost: tells which flows hit this
      this.report("case_virtual_fs_emptied", payload)
      await this.fsUnlink()
      this._setActiveCase(caseId)
      return true
    }
    // loaded or written by a path that doesn't pass a caseId, in the same flow
    this._setActiveCase(caseId)
    return false
  }

  private _markHydrated(keys: string[]) {
    for (const key of keys) {
      const name = toZipBaseName(key)
      if (FS_FILEZIP.includes(name)) this._hydratedZips.add(name)
    }
  }

  /**
   * Guarded zips that exist on the server but were never loaded in this session:
   * rebuilding them from the virtual FS would overwrite the server copy with
   * only the files written locally (plus default resources).
   * An unknown server list counts as "exists", unless nothing was loaded at all
   * and the caller doesn't know the case exists (new case creation, the virtual
   * FS is the only source).
   * @param caseId case the zips are built for, the virtual FS owner by default
   */
  getUnloadedServerZips(
    zipNames: string[],
    isExistingCase = false,
    caseId?: string,
  ): string[] {
    const serverZips = this._getServerZips(caseId)
    const hydratedZips = this.getHydratedZips(caseId)
    const isNewCase =
      !isExistingCase && serverZips === null && hydratedZips.length === 0
    if (isNewCase) return []
    return zipNames.filter(
      (name) =>
        OVERWRITE_GUARDED_ZIPS.includes(name) &&
        !hydratedZips.includes(name) &&
        (serverZips === null || serverZips.has(name)),
    )
  }
  /**
   * 将文件写入相应虚拟目录
   * @param writeFileList Record<string, Uint8Array> key 为文件路径名
   */
  async moduleFS(writeFileList: Record<string, any>) {
    console.log("make virtual directory:", writeFileList)
    if (!this.ismkdirdone) await this._fsMkdir()
    // 构建wasm需要的目录字典
    // 写入数据
    const keys = Object.keys(writeFileList)
    //if (!this.ishismkdirdone) await this._historyMkdir(keys)
    await this._historyMkdir(keys)
    for (let index = 0; index < keys.length; index++) {
      const key = keys[index]
      try {
        // 过滤非resource中的文件
        let num = 0
        for (let i = 0; i < FS_RESOURCE_TO_CASE.length; i++) {
          const caseresource: string = FS_RESOURCE_TO_CASE[i]
          num = key.indexOf(caseresource)
          if (num !== -1) break
        }
        if (num !== -1) continue
        const ab = await writeFileList[key].arrayBuffer()
        const arrayBuffer = new Uint8Array(ab)
        wasmModule.module.FS.writeFile(key, arrayBuffer)
        // console.log("\t\t correct!")
      } catch (excp) {
        console.warn("write virtual file false::", key, excp)
      }
    }
    return true
  }

  async moduleFSPrevCase(writeFileList: Record<string, any>) {
    const keys = Object.keys(writeFileList)
    this._mkDirByKeys(keys)
    for (let index = 0; index < keys.length; index++) {
      const key = keys[index]
      try {
        // 过滤非resource中的文件
        let num = 0
        for (let i = 0; i < FS_RESOURCE_TO_CASE.length; i++) {
          const caseresource: string = FS_RESOURCE_TO_CASE[i]
          num = key.indexOf(caseresource)
          if (num !== -1) break
        }
        if (num !== -1) continue
        const ab = await writeFileList[key].arrayBuffer()
        const arrayBuffer = new Uint8Array(ab)
        wasmModule.module.FS.writeFile(key, arrayBuffer)
        // console.log("\t\t correct!")
      } catch (excp) {
        console.warn("write virtual file false::", key, excp)
      }
    }
    return true
  }

  /**
   * get dir list from vitural FS
   * @param relatePath relate to the base path "test/case"
   */
  getDirInVituralFS(relatePath?: CaseZipType) {
    if (relatePath) {
      return wasmModule.module.FS.readdir(`${FS_BASEPATH}/${relatePath}`)
    } else {
      return wasmModule.module.FS.readdir(`${FS_BASEPATH}`)
    }
  }

  private _cacheDataVersionInfo = undefined

  /**
   * cache data version for check if need to upload,
   * it needs to be invoked when loading case is finished and when uploading is finished.
   *
   */
  saveDataVersionInfo() {
    this._cacheDataVersionInfo = this.getDataVersionInfo()
  }

  /**
   * it needs to be invoked during closing of the case.
   */
  async clear() {
    this._cacheDataVersionInfo = undefined
    await this.fsUnlink()
  }

  /**
   * get every dir version info
   * A larger value indicates a larger version
   * @returns
   */
  getDataVersionInfo(): {
    bulk0Version: number
    bulk1Version: number
    bulk2Version: number
    bulk10Version: number
    photoVersion: number
    rawVersion: number
  } {
    const bulk0VersionJson = this.getFileDirectly(
      "Setting Data0.version.json",
      "Setting Data0",
      "utf8",
    )
    const bulk1VersionJson = this.getFileDirectly(
      "Setting Data1.version.json",
      "Setting Data1",
      "utf8",
    )
    const bulk2VersionJson = this.getFileDirectly(
      "Setting Data2.version.json",
      "Setting Data2",
      "utf8",
    )
    const bulk10VersionJson = this.getFileDirectly(
      "Setting Data10.version.json",
      "Setting Data10",
      "utf8",
    )
    const photoVersionJson = this.getFileDirectly(
      "Photo.version.json",
      "Photo",
      "utf8",
    )

    const rawVersionJson = this.getFileDirectly(
      "Raw.version.json",
      "Raw",
      "utf8",
    )

    let bulk0Version,
      bulk1Version,
      bulk2Version,
      bulk10Version,
      photoVersion,
      rawVersion

    try {
      bulk0Version = bulk0VersionJson ? JSON.parse(bulk0VersionJson) : null
    } catch (err) {
      console.warn(err)
      bulk0Version = null
    }

    try {
      bulk1Version = bulk1VersionJson ? JSON.parse(bulk1VersionJson) : null
    } catch (err) {
      console.warn(err)
      bulk1Version = null
    }

    try {
      bulk2Version = bulk2VersionJson ? JSON.parse(bulk2VersionJson) : null
    } catch (err) {
      console.warn(err)
      bulk2Version = null
    }

    try {
      bulk10Version = bulk10VersionJson ? JSON.parse(bulk10VersionJson) : null
    } catch (err) {
      bulk10Version = null
      console.warn(err)
    }

    try {
      photoVersion = photoVersionJson ? JSON.parse(photoVersionJson) : null
    } catch (err) {
      console.warn(err)
      photoVersion = null
    }

    try {
      rawVersion = rawVersionJson ? JSON.parse(rawVersionJson) : null
    } catch (err) {
      console.warn(err)
      rawVersion = null
    }

    if (bulk0Version && bulk0Version.version) {
      bulk0Version = bulk0Version.version
    } else {
      bulk0Version = null
    }

    if (bulk1Version && bulk1Version.version) {
      bulk1Version = bulk1Version.version
    } else {
      bulk1Version = null
    }
    if (bulk2Version && bulk2Version.version) {
      bulk2Version = bulk2Version.version
    } else {
      bulk2Version = null
    }
    if (bulk10Version && bulk10Version.version) {
      bulk10Version = bulk10Version.version
    } else {
      bulk10Version = null
    }
    if (photoVersion && photoVersion.version) {
      photoVersion = photoVersion.version
    } else {
      photoVersion = null
    }

    if (rawVersion && rawVersion.version) {
      rawVersion = rawVersion.version
    } else {
      rawVersion = null
    }

    return {
      bulk0Version,
      bulk1Version,
      bulk2Version,
      bulk10Version,
      photoVersion,
      rawVersion,
    }
  }

  /**
   * check if there are some dir need to upload?
   *
   * @returns
   */
  checkWhatHadBeenModified(): ZipFileName[] {
    const newVersions = this.getDataVersionInfo()

    const ret = []
    const keys = Object.keys(newVersions)

    for (const key of keys) {
      const version = newVersions[key]

      //  tag it if it's not included in cache
      if (!this._cacheDataVersionInfo || !this._cacheDataVersionInfo[key]) {
        ret.push(key.split("Version")[0])
      } else {
        if (this._cacheDataVersionInfo[key] < version) {
          ret.push(key.split("Version")[0])
        }
      }
    }

    return ret
  }

  /**
   * Is the path Exist ??
   * @param relatePath
   * @returns
   */
  isPathExist(relatePath?: CaseZipType): boolean {
    if (relatePath) {
      return wasmModule.module.FS.analyzePath(`${FS_BASEPATH}/${relatePath}`)
        .exists
    } else {
      return wasmModule.module.FS.analyzePath(`${FS_BASEPATH}`).exists
    }
  }

  /**
   * get file information list from a Zip File
   * @param zipfile
   * @param outFiles
   * @param path
   * @returns
   */
  private getFilesFromZip(
    zipfile: File,
    outFiles: Record<string, any>,
    path?: RegExp,
  ) {
    return JSZip.loadAsync(zipfile)
      .then(async (zip: any) => {
        return Promise.all(this.loadFiles(zip, outFiles, path))
      })
      .catch((reason) => {
        console.warn("uncompress zip false:", reason)
      })
  }
  /**
   * get files from zip
   * @param zip
   * @param out
   * @param path
   * @returns
   */
  private loadFiles(
    zip,
    out: Record<string, Blob>,
    path?: RegExp,
  ): Promise<void>[] {
    const promises: any[] = []
    path = path || new RegExp("")
    const files = path ? zip.file(path) : zip
    // console.log('>>>@@@', path, zip, files);
    files.forEach((fileProto) => {
      if (!fileProto.dir) {
        promises.push(
          zip
            .file(fileProto.name)
            .async("blob")
            .then((blob) => {
              if (fileProto.name === "undefined") {
                return
              }
              fileProto.name.sub()

              const startIndex = fileProto.name.search(path)
              let fileName = fileProto.name
              if (startIndex > 0) {
                fileName = (fileProto.name as string).substring(startIndex)
              }
              const fileblob = new File([blob], fileName)
              out[fileName] = fileblob
            }),
        )
      }
    })
    return promises
  }

  /**
   * write case files to virtual FS in WASM
   * @param caseZipFiles
   */
  /**
   * @param options.caseId owner of the zips: the virtual FS is emptied first
   *   when it holds another case
   * @param options.keepExistingFiles don't overwrite files already in the
   *   virtual FS (local writes win), only applies to the same case
   */
  async writeCaseFilesToVirtualFS(
    caseZipFiles: Record<string, Blob>,
    options?: { caseId?: string; keepExistingFiles?: boolean },
  ) {
    if (isValidCaseRef(options?.caseId)) {
      await this._claimVirtualFS(options.caseId)
    }
    const filelist = await this.turnZipFilesIntoFileDirectory(caseZipFiles)
    const out = await this.makeVirtualDirectory(filelist)
    if (options?.keepExistingFiles) {
      for (const path of Object.keys(out)) {
        if (wasmModule.module.FS.analyzePath(path).exists) delete out[path]
      }
    }
    await this.moduleFS(out)
  }

  /**
   * write case files to virtual FS in WASM
   * @param caseZipFiles
   */
  async writePrevCaseFilesToVirtualFS(caseZipFiles: Record<string, Blob>) {
    // written under /test/prevcase, it doesn't hydrate the current case
    const filelist = await this.turnZipFilesIntoFileDirectory(
      caseZipFiles,
      false,
    )
    const out = await this.makeVirtualDirectoryPrevCase(filelist)
    await this.moduleFS(out)
  }

  /**
   * Create a file directory from files in Map, it's used to make file dir in WASM .
   */
  async turnZipFilesIntoFileDirectory(
    fileBlobRecord: Record<string, Blob>,
    isCaseHydration = true,
  ) {
    const outFiles: Record<string, Record<string, any>> = {}
    const keys = Object.keys(fileBlobRecord)
    if (isCaseHydration) {
      this._markHydrated(keys.filter((key) => fileBlobRecord[key]))
    }
    for (const key of keys) {
      // console.log("uncompress zip >>>", key)

      const res = fileBlobRecord[key]
      if (!res) continue

      if (key.includes("RetouchHistory.json")) {
        const fileList: Record<string, any> = {}
        fileList["RetouchHistory/RetouchHistory.json"] = blobToFile(res, key)
        outFiles["RetouchHistory.json"] = fileList
      } else {
        const fileList: Record<string, any> = {}
        await this.getFilesFromZip(blobToFile(res, key), fileList)
        outFiles[key] = fileList
      }
    }
    console.log("uncompress out >>>", outFiles)
    return outFiles
  }

  /**
   * write state resource file into virtual file system
   */
  async writeStaticResourceToVirtualFileSystem() {
    const writestaticFileList = await this._getStaticResourceDirectory()
    await this.moduleFS(writestaticFileList)
  }

  /**
   * Get static resource directory for make directory in WASM
   * @returns
   */
  /** static resource directory, {} when it is missing or unreadable */
  private async _readStaticResourceDirectory(): Promise<Record<string, File>> {
    try {
      return await this._getStaticResourceDirectory()
    } catch (err) {
      console.warn("[zip-guard] static resources unreadable:", err)
      return {}
    }
  }

  /**
   * Static resource directory, reloaded once when the default case files are
   * missing: without them, empty case folders are uploaded with no
   * "<folder>.version.json" and storage.version.json is uploaded empty.
   */
  private async _ensureStaticResourceDirectory(): Promise<
    Record<string, File>
  > {
    const directory = await this._readStaticResourceDirectory()
    if (directory[FS_RESOURCE_TO_CASEFILE[0]]) return directory

    const isRetryDelayed =
      this._staticReloadFailedAt > 0 &&
      Date.now() - this._staticReloadFailedAt < STATIC_RESOURCE_RETRY_DELAY_MS
    if (isRetryDelayed) return directory

    if (!this._staticReload) {
      this._staticReload = this._reloadStaticResourceDirectory(
        directory,
      ).finally(() => {
        this._staticReload = null
      })
    }
    return this._staticReload
  }

  private async _reloadStaticResourceDirectory(
    current: Record<string, File>,
  ): Promise<Record<string, File>> {
    this.report("case_static_resources_missing", { action: "reload" })
    // the save waits for the reload: tells RUM how long it blocks
    const startedAt = Date.now()
    const fail = (errorMessage: string) => {
      this._staticReloadFailedAt = Date.now()
      this.report("case_static_resources_unavailable", {
        errorMessage: errorMessage.slice(0, 200),
        durationMs: Date.now() - startedAt,
      })
    }
    try {
      await resourceManager.reloadStaticResource()
    } catch (err) {
      console.error("[zip-guard] static resources reload failed:", err)
      fail(err instanceof Error ? err.message : String(err))
      return current
    }
    const reloaded = await this._readStaticResourceDirectory()
    if (!reloaded[FS_RESOURCE_TO_CASEFILE[0]]) {
      fail("reloaded resource.zip has no default case files")
      return reloaded
    }
    this._staticReloadFailedAt = 0
    this.report("case_static_resources_reloaded", {
      durationMs: Date.now() - startedAt,
    })
    return reloaded
  }

  private async _getStaticResourceDirectory() {
    const fileDirMap = resourceManager.getResourceStatic()
    const filelist = await resourceManager.turnBlobMapIntoFileDirectory(
      fileDirMap,
    )
    const out = await this.makeVirtualDirectory(filelist)
    return out
  }

  /**
   * Get case resource directory for make directory in WASM
   * @returns
   */
  async getCaseResourceDirectory() {
    const fileDirMap = resourceManager.getCaseResource()
    const filelist = await resourceManager.turnBlobMapIntoFileDirectory(
      fileDirMap,
    )
    const out = await this.makeVirtualDirectory(filelist)
    return out
  }

  // 递归遍历某个虚拟目录获取目录下所有文件路径名（包括子目录）并放入一个List中。
  private async _getFileListRecursivelyFromVirtualDir(
    out: string[],
    dirName: string,
  ) {
    try {
      const dir = wasmModule.module.FS.readdir(`${FS_BASEPATH}/${dirName}`)
      for (let l = 0; l < dir.length; l++) {
        if (dir[l] === "." || dir[l] === "..") continue
        const fullFilePath = `${FS_BASEPATH}/${dirName}/${dir[l]}`
        const stats = wasmModule.module.FS.stat(fullFilePath)
        if (stats && wasmModule.module.FS.isDir(stats.mode)) {
          //console.log("路径是一个目录");
          await this._getFileListRecursivelyFromVirtualDir(
            out,
            `${dirName}/${dir[l]}`,
          )
        } else if (stats && wasmModule.module.FS.isFile(stats.mode)) {
          const tempkey = `${dirName}/${dir[l]}`
          out.push(tempkey)
          //console.log("路径是一个文件", tempkey, out);
        }
      }
    } catch (excp) {
      console.log(
        `Get filelist from VM Dir '${FS_BASEPATH}/${dirName}'error:`,
        excp,
      )
    }
  }

  /**
   * 获取FS_BASEPATH路径下的所有文件列表，包含完整路径信息
   * @param dirName 可选参数，指定要获取的子目录名称，默认为空（即FS_BASEPATH根目录）
   *     Setting Data0、Setting Data1、Setting Data2、Setting Data10、Photo、Raw等
   * @returns 文件路径列表，每个文件路径包含相对于FS_BASEPATH的完整路径信息
   */
  async getFileListWithFullPath(dirName: string = ""): Promise<string[]> {
    const fileList: string[] = []
    try {
      // 如果指定了子目录，则获取该子目录下的文件列表
      if (dirName) {
        await this._getFileListRecursivelyFromVirtualDir(fileList, dirName)
      } else {
        // 否则获取FS_BASEPATH根目录下的所有文件
        // 首先读取根目录下的所有项目
        const rootItems = wasmModule.module.FS.readdir(FS_BASEPATH)
        for (const item of rootItems) {
          if (item === "." || item === "..") continue
          const fullPath = `${FS_BASEPATH}/${item}`
          const stats = wasmModule.module.FS.stat(fullPath)

          if (wasmModule.module.FS.isDir(stats.mode)) {
            // 如果是目录，则递归获取其下的文件
            await this._getFileListRecursivelyFromVirtualDir(fileList, item)
          } else if (wasmModule.module.FS.isFile(stats.mode)) {
            // 如果是文件，则直接添加到列表中
            fileList.push(item)
          }
        }
      }
    } catch (error) {
      console.error(`获取文件列表失败:`, error)
    }
    return fileList
  }

  /**
   * Remove files from the WASM virtual filesystem
   */
  async fsUnlink() {
    this.resetHydration()
    this._activeCaseId = null
    if (!wasmModule.module) return
    console.log("fsUnlink: clear case resource.")
    try {
      await this._deleteFolderRecursive(`${FS_BASEPATH}/${"RetouchHistory"}`)
    } catch (err) {
      console.error("Delete virtual folder 'retouchHistory' false:", err)
    }

    const keys = FS_FILEZIP
    for (let i = 0; i < keys.length; i++) {
      let name = keys[i]
      if (keys[i] === "bulk0") name = "Setting Data0"
      if (keys[i] === "bulk2") name = "Setting Data2"
      if (keys[i] === "bulk1") name = "Setting Data1"
      if (keys[i] === "bulk10") name = "Setting Data10"
      if (keys[i] === "raw") name = "Raw"
      if (keys[i] === "photo") name = "Photo"
      const out = []
      await this._getFileListRecursivelyFromVirtualDir(out, name)
      for (let n = 0; n < out.length; n++) {
        try {
          const fullFilePath = `${FS_BASEPATH}/${out[n]}`
          await wasmModule.module.FS.unlink(fullFilePath)
        } catch (err) {
          console.warn(`Unlink false [${FS_BASEPATH}/${out[n]}]:`, err)
        }
      }
    }
  }

  /**
   * make the path of resource in virtual FS
   * @param fileList
   * @returns
   */
  async makeVirtualDirectoryPath(
    casePath: string,
    fileList: Record<string, Record<string, Blob>>,
  ) {
    const writeFileList: Record<string, File> = {}
    const keys = Object.keys(fileList)
    for (let i = 0; i < keys.length; i++) {
      const files = fileList[keys[i]]
      let setting = ""
      switch (keys[i]) {
        case "resource":
          setting = "/"
          break
        default:
          setting = "/" + casePath + "/"
          break
      }
      const itemkeys = Object.keys(files)
      for (let l = 0; l < itemkeys.length; l++) {
        const res = blobToFile(files[itemkeys[l]], itemkeys[l])
        // const ab = await res.arrayBuffer();
        // res = new Uint8Array(ab);
        writeFileList[`${`/test${setting}`}${itemkeys[l]}`] = res
      }
    }
    return writeFileList
  }

  /**
   * make the path of resource in virtual FS
   * @param fileList
   * @returns
   */
  async makeVirtualDirectory(fileList: Record<string, Record<string, Blob>>) {
    return this.makeVirtualDirectoryPath("case", fileList)
  }

  /**
   * make vitural FS of "prev case" for retouch v2
   * make the path of resource in virtual FS
   * @param fileList
   * @returns
   */
  async makeVirtualDirectoryPrevCase(
    fileList: Record<string, Record<string, Blob>>,
  ) {
    console.log(wasmModule.module.FS.readdir("/test"))
    this._fsRmdir("/test/prevcase")
    console.log(wasmModule.module.FS.readdir("/test"))
    return this.makeVirtualDirectoryPath("prevcase", fileList)
  }

  private _fsRmdir(dir: string) {
    let files
    try {
      files = wasmModule.module.FS.readdir(dir)
    } catch (e) {
      console.log("_fsRmdir err: " + e)
      return
    }
    for (const file of files) {
      if (file === "." || file === "..") continue
      const fullPath = dir + "/" + file
      const stat = wasmModule.module.FS.stat(fullPath)
      if (wasmModule.module.FS.isDir(stat.mode)) {
        this._fsRmdir(fullPath)
      } else {
        wasmModule.module.FS.unlink(fullPath)
      }
    }
    wasmModule.module.FS.rmdir(dir)
  }

  /**
   * 构建虚拟文件系统中需要的目录
   */
  private async _fsMkdir() {
    for (let i = 0; i < FS_MKDIR.length; i++) {
      await wasmModule.module.FS.mkdir(FS_MKDIR[i])
    }
    this.ismkdirdone = true
  }
  /**
   *  获取wasm保存的照片
   * @returns
   */
  getScansPhoto() {
    const photo: Map<string, File> = new Map<string, File>()
    for (let i = 0; i < SCANS_PHOTO.length; i++) {
      if (wasmModule.module.FS.analyzePath(SCANS_PHOTO[i]).exists) {
        const dir: Blob = wasmModule.module.FS.readFile(SCANS_PHOTO[i])
        const scansNames: string[] = SCANS_PHOTO[i].split("/")
        const file: File = new File([dir], scansNames[scansNames.length - 1], {
          type: "image/png",
          lastModified: Date.now(),
        })
        photo.set(scansNames[scansNames.length - 1], file)
      }
    }
    return photo
  }

  getInitialFinalScreenshot() {
    const photo: Map<string, File> = new Map<string, File>()
    wasmModule.stagingcontoler.GetInitAndFinalStagePNG(true)
    wasmModule.stagingcontoler.GetInitAndFinalStagePNG(false)
    for (let i = 0; i < SCREEN_SHOT.length; i++) {
      if (wasmModule.module.FS.analyzePath(SCREEN_SHOT[i]).exists) {
        const dir: Blob = wasmModule.module.FS.readFile(SCREEN_SHOT[i])
        const scansNames: string[] = SCREEN_SHOT[i].split("/")
        const file: File = new File([dir], scansNames[scansNames.length - 1], {
          type: "image/png",
          lastModified: Date.now(),
        })
        photo.set(scansNames[scansNames.length - 1], file)
      }
    }
    return photo
  }

  /**
   * 获取指定pathname的文件
   * @param pathName
   * @returns file of FILE type
   */
  getFileByPathname(pathName: string): File | null {
    if (wasmModule.module.FS.analyzePath(pathName).exists) {
      const blob: Blob = wasmModule.module.FS.readFile(pathName)

      const fileName = pathName.split("/").pop()

      let fileExt = fileName.split(".").pop() as string
      fileExt = fileExt.toLocaleLowerCase()
      let fileType = "blob"
      if (["jpeg", "jpg", "png", "bmp"].includes(fileExt)) {
        fileType = "image/png"
      }

      const file: File = new File([blob], fileName, {
        lastModified: Date.now(),
        type: fileType,
      })
      return file
    }
    return null
  }

  /**
   * 获取指定pathname list的文件列表
   * @param pathNames string[] pathName list
   */
  getFilesByPathname(pathNames: string[]): Map<string, File> {
    const files: Map<string, File> = new Map<string, File>()
    for (let i = 0; i < pathNames.length; i++) {
      const file = this.getFileByPathname(pathNames[i])
      const filename: string = pathNames[i].split("/").pop()
      if (file) {
        files.set(filename, file)
      } else {
        console.warn("File not exist:", pathNames[i])
        files.set(filename, null)
      }
    }
    return files
  }

  /**
   * 删除指定pathname的文件
   * @param pathName
   * @returns
   */
  deleteFile(pathName: string) {
    console.log("deleteFile:", pathName)
    if (!wasmModule.module) return

    if (wasmModule.module.FS.analyzePath(pathName).exists) {
      wasmModule.module.FS.unlink(pathName)
      console.log("deleteFile successful")
    }
  }

  /**
   * 直接获取虚拟内存中某个文件，当文件encoding是utf8的时候返回文本字符串，
   * 当encoding是binary的时候，返回UInt8Array
   * @param fileName
   * @param pathName
   * @param encoding
   * @returns
   */
  getFileDirectly(
    fileName: string,
    pathName: CaseZipType,
    encoding?: "binary" | "utf8",
  ) {
    const dir = `${FS_BASEPATH}/${pathName}/${fileName}`
    if (wasmModule.module.FS.analyzePath(dir).exists) {
      const ret = wasmModule.module.FS.readFile(dir, {
        encoding: encoding ? encoding : "binary",
      })
      return ret
    } else {
      console.warn("getFileDirectly: file path is not exist.")
    }
    return null
  }

  /**
   * Get a file from virtual file system
   */
  async getFileFromZip(fileName: string, path: CaseZipType) {
    const fullFilePath = `${FS_BASEPATH}/${path}/${fileName}`
    const file = await wasmModule.module.FS.readFile(fullFilePath)
    return file as File
  }
  /**
   * List files in folder from virtual file system
   */
  getFileListFromZip(path: CaseZipType) {
    const dir = wasmModule.module.FS.readdir(`${FS_BASEPATH}/${path}`)
    let fileList: string[] = []
    for (let l = 0; l < dir.length; l++) {
      const fileName = dir[l]
      if (fileName === "." || fileName === "..") continue
      const fullFilePath = `${FS_BASEPATH}/${path}/${fileName}`
      const stats = wasmModule.module.FS.stat(fullFilePath)
      if (stats && wasmModule.module.FS.isDir(stats.mode)) continue
      var fileExt = fileName.split(".").pop() as string
      fileExt = fileExt.toLocaleLowerCase()
      if (
        fileExt === "jpeg" ||
        fileExt === "jpg" ||
        fileExt === "png" ||
        fileExt === "bmp"
      )
        fileList.push(dir[l] as string)
    }
    return fileList
  }

  /**
   * 将数据存放至虚拟目录
   * @param data
   * @param fileName
   * @param path
   * @returns
   */
  async saveJsonDataToZip(data: string, fileName: string, path: CaseZipType) {
    const blob = new Blob([data], { type: "text/plain" })
    const file = new File([blob], `case/${path}/${fileName}`)
    const savePath: Record<string, any> = {}
    savePath[`test/case/${path}/${fileName}`] = file

    const saveRes = await resourcesSynchronization.moduleFS(savePath)
    return saveRes
  }

  /**
   * Save a file to virtual file system
   */
  async saveFileToZip(file: File, fileName: string, path: CaseZipType) {
    const savePath: Record<string, any> = {}
    savePath[`test/case/${path}/${fileName}`] = file
    const saveRes = await resourcesSynchronization.moduleFS(savePath)
    return saveRes
  }

  /**
   * 前端调用save数据后获取所有case zip文件，并将这些文件打包到一个zip中
   * （方便调试）
   * @params isNeedDownload 是否下载，是的话会同时下载这个zip文件
   */
  async saveAllToOneZip(
    isNeedDownload?: boolean,
    fileNames?: ZipFileName | ZipFileList,
    downloadZipName?: string,
  ) {
    const files = await this.saveAndGetFileZips(fileNames, false, {
      guard: "skip",
    })
    const zip = new JSZip()
    const keys = Object.keys(files)

    // create a root folder, all files are saved in it.
    const rootFolder = zip.folder("savecase")
    for (const key of keys) {
      const file = files[key]
      rootFolder.file(file.name, file, {
        binary: true,
      })
    }

    return new Promise((resolve) => {
      zip.generateAsync({ type: "blob" }).then((content) => {
        const file: File = new File([content], `savecase.zip`, {
          type: content.type,
          lastModified: Date.now(),
        })

        if (isNeedDownload) {
          this.download(
            downloadZipName ? downloadZipName : `savecase.zip`,
            content,
          )
        }
        resolve(file)
      })
    })
  }

  /**
   * 前端调用save数据后获取所有case zip文件，并将这些文件打包到一个zip中
   * 同时下载这个zip文件
   */
  async downloadCaseZip(
    zipName: string,
    fileNames?: ZipFileName | ZipFileList,
  ) {
    const files = await this.saveAndGetFileZips(fileNames, false, {
      guard: "skip",
    })
    const zip = new JSZip()
    const keys = Object.keys(files)
    // create a root folder, all files are saved in it.
    const rootFolder = zip.folder("savecase")
    for (const key of keys) {
      const file = files[key]
      rootFolder.file(file.name, file, {
        binary: true,
      })
    }
    return new Promise((resolve) => {
      zip.generateAsync({ type: "blob" }).then((content) => {
        const file: File = new File([content], `savecase.zip`, {
          type: content.type,
          lastModified: Date.now(),
        })
        this.download(zipName, content)
        resolve(file)
      })
    })
  }

  // 从某个虚拟目录拿取下面所有文件的列表，包括子目录
  async getFileListFromVirtualFS(rootDirName: string) {
    const files: Record<string, File> = {}
    const fileList = []
    await this._getFileListRecursivelyFromVirtualDir(fileList, rootDirName)
    for (let index = 0, len = fileList.length; index < len; index++) {
      const pathName = fileList[index]
      const fullFilePath = `${FS_BASEPATH}/${pathName}`
      files[pathName] = await wasmModule.module.FS.readFile(fullFilePath)
    }
    return files
  }

  async zipFileListFromVirtualFS(files: Record<string, Record<string, File>>) {
    const ziplist: Record<string, File> = {}
    const list: Promise<void>[] = []
    let zipFileKeys = Object.keys(files)

    // 把文件列表压缩到各个zip中
    for (let i = 0; i < zipFileKeys.length; i++) {
      if (zipFileKeys[i] === "storage.version.json") continue

      const zip = new JSZip()
      const zipKeys = Object.keys(files[zipFileKeys[i]])
      // const promises = [] as any;
      for (let l = 0; l < zipKeys.length; l++) {
        zip.file(zipKeys[l], files[zipFileKeys[i]][zipKeys[l]], {
          binary: true,
        })
      }

      const zipdata: Promise<void> = zip
        .generateAsync({ type: "blob" })
        .then((content) => {
          // zip.generateAsync({ type: 'arraybuffer', compression: 'DEFLATE' }).then((content) => {
          // FileSaver.saveAs(content, `${keys[i]}.zip`) // 利用file-saver保存文件  自定义文件名
          // console.log('content', content, `${keys[i]}.zip`);
          const file: File = new File([content], `${zipFileKeys[i]}.zip`, {
            type: content.type,
            lastModified: Date.now(),
          })
          ziplist[`${zipFileKeys[i]}.zip`] = file
          //          if (isNeedDownload) {
          //            this.download(`${zipFileKeys[i]}.zip`, content)
          //          }
        })
      list.push(zipdata)
    }

    // end
    return Promise.all(list).then((res) => {
      return ziplist
    })
  }

  /**
   * 前端调用后触发wasm数据保存，保存数据后js获取数据文件，并通过zip压缩返回给前端
   * @params isNeedDownload 是否下载，是的话会同时下载这些zip文件
   * @returns
   */
  async saveAndGetFileZips(
    fileNames?: ZipFileName | ZipFileList,
    isNeedDownload?: boolean,
    options?: SaveAndGetFileZipsOptions,
  ) {
    const guard: ZipOverwriteGuardMode =
      options?.guard ?? (fileNames ? "check" : "allowRebuild")

    // 先保存文件
    if (wasmModule.stagingcontoler) {
      wasmModule.stagingcontoler.SaveSetUpResult()
    }

    let zipFileKeys = FS_FILEZIP

    if (fileNames) {
      // 指定读某一个ZIP文件
      if (typeof fileNames === "string") {
        const index = FS_FILEZIP.findIndex((val) => {
          return val === fileNames
        })
        if (index !== -1) {
          zipFileKeys = [fileNames]
        }
      } else if (Array.isArray(fileNames) && fileNames.length > 0) {
        zipFileKeys = fileNames
      }
    }

    let unloadedServerZips: string[] = []
    if (guard === "check") {
      const guarded = this._guardUnloadedServerZips(zipFileKeys, options)
      zipFileKeys = guarded.zipNames
      unloadedServerZips = guarded.unloadedZips
    }

    // 补齐必须的 json 文件（若缺失则使用默认文件）
    const staticFileList = await this._ensureStaticResourceDirectory()
    const resourceKeys = Object.keys(staticFileList)

    for (const resourceKey of resourceKeys) {
      // 找到匹配的资源目录
      const matchedDir = FS_RESOURCE_TO_CASE.find((dir) =>
        resourceKey.includes(`/${dir}/`),
      )
      if (!matchedDir) continue

      const casePath = resourceKey.replace("resource", "case")

      // 已存在则无需写入
      if (wasmModule.module.FS.analyzePath(casePath).exists) continue

      let lastName = matchedDir.split("/").pop() || ""
      lastName = lastName.replace("Setting Data", "bulk").toLowerCase()
      // 不在需要打包的zip列表中则跳过
      if (!zipFileKeys.includes(lastName)) {
        console.log("Skip saving this file:", casePath)
        continue
      }

      // 写入默认文件
      const buffer = new Uint8Array(
        await staticFileList[resourceKey].arrayBuffer(),
      )
      console.log("save default json file:", casePath)
      wasmModule.module.FS.writeFile(casePath, buffer)
    }

    const files: Record<string, Record<string, File>> = {}
    for (let i = 0; i < zipFileKeys.length; i++) {
      const name = CASE_ZIP_FOLDERS[zipFileKeys[i]]
      if (!name) continue

      files[zipFileKeys[i]] = await this.getFileListFromVirtualFS(name)
    }
    console.log("ashuaizzz🚀 ~console all files in virtual memory:", files)

    const ziplist: Record<string, File> = {}
    const list: Promise<void>[] = []

    // 把文件列表压缩到各个zip中
    for (let i = 0; i < zipFileKeys.length; i++) {
      if (zipFileKeys[i] === "storage.version.json") continue

      const zip = new JSZip()
      if (files[zipFileKeys[i]] == undefined || files[zipFileKeys[i]] == null) {
        console.log("missing files[zipFileKeys[i]] :", zipFileKeys[i])
        continue
      }
      // an empty folder must still be extracted: weDesign refuses a case
      // missing one ("Local case file is not complete.")
      zip.folder(CASE_ZIP_FOLDERS[zipFileKeys[i]])
      const zipKeys = Object.keys(files[zipFileKeys[i]])
      // const promises = [] as any;
      for (let l = 0; l < zipKeys.length; l++) {
        zip.file(zipKeys[l], files[zipFileKeys[i]][zipKeys[l]], {
          binary: true,
        })
      }

      const zipdata: Promise<void> = zip
        .generateAsync({ type: "blob" })
        .then((content) => {
          // zip.generateAsync({ type: 'arraybuffer', compression: 'DEFLATE' }).then((content) => {
          // FileSaver.saveAs(content, `${keys[i]}.zip`) // 利用file-saver保存文件  自定义文件名
          // console.log('content', content, `${keys[i]}.zip`);
          const file: File = new File([content], `${zipFileKeys[i]}.zip`, {
            type: content.type,
            lastModified: Date.now(),
          })
          ziplist[`${zipFileKeys[i]}.zip`] = file
          if (isNeedDownload) {
            this.download(`${zipFileKeys[i]}.zip`, content)
          }
        })
      list.push(zipdata)
    }

    if (zipFileKeys.includes("storage.version.json")) {
      // 补齐UD需要的storage.version.json.zip文件
      // const promises = [] as any;
      const zip = new JSZip()
      for (let l = 0; l < FS_RESOURCE_TO_CASEFILE.length; l++) {
        const path = FS_RESOURCE_TO_CASEFILE[l].replace("/test/resource/", "")
        zip.file(path, staticFileList[FS_RESOURCE_TO_CASEFILE[l]], {
          binary: true,
        })
        // promises.push(promise);
        const zipdata: Promise<void> = zip
          .generateAsync({ type: "blob" })
          .then((content) => {
            const pathsplit = FS_RESOURCE_TO_CASEFILE[0].split("/")
            const name = pathsplit[pathsplit.length - 1]
            const file: File = new File([content], `${name}.zip`, {
              type: content.type,
              lastModified: Date.now(),
            })
            ziplist[`${name}.zip`] = file
            if (isNeedDownload) {
              this.download(`${name}.zip`, content)
            }
          })
        list.push(zipdata)
      }
    }

    // end
    return Promise.all(list).then((res) => {
      // the virtual FS is now the source of truth for these zips: later
      // partial rebuilds in the same session must not be dropped
      // ...except the zips rebuilt without their server copy (log mode): a
      // later ensureCaseZipsLoaded must still load them
      if (guard !== "skip") {
        this._markHydrated(
          Object.keys(ziplist).filter(
            (key) => !unloadedServerZips.includes(toZipBaseName(key)),
          ),
        )
      }
      return ziplist
    })
  }

  /**
   * @returns the zip names to build, and the ones the server has but this
   *   session never loaded
   */
  private _guardUnloadedServerZips(
    zipNames: string[],
    options?: SaveAndGetFileZipsOptions,
  ): { zipNames: string[]; unloadedZips: string[] } {
    const unloaded = this.getUnloadedServerZips(
      zipNames,
      options?.isExistingCase,
      options?.caseId,
    )
    if (unloaded.length === 0) return { zipNames, unloadedZips: [] }

    const isDropped = this.zipGuardAction === "drop"
    const payload = {
      unloadedZips: unloaded,
      requestedZips: zipNames,
      hydratedZips: this.getHydratedZips(options?.caseId),
      isServerListKnown: this._getServerZips(options?.caseId) !== null,
      activeCaseId: this._activeCaseId,
      targetCaseId: options?.caseId ?? null,
      isDropped,
    }
    console.error(
      isDropped
        ? "[zip-guard] not rebuilding zips that were never loaded in this session, it would overwrite the server copy:"
        : "[zip-guard] rebuilding zips that were never loaded in this session, the server copy may be overwritten:",
      payload,
    )
    this.report("case_zip_overwrite_detected", payload)
    return {
      zipNames: isDropped
        ? zipNames.filter((name) => !unloaded.includes(name))
        : zipNames,
      unloadedZips: unloaded,
    }
  }
  /**
   * 保存至本地
   * @param name
   * @param data
   */
  download(name, data) {
    const urlObject: any = window.URL || window.webkitURL || window

    const downloadData = new Blob([data])

    const save_link: any = document.createElementNS(
      "http://www.w3.org/1999/xhtml",
      "a",
    )
    save_link.href = urlObject.createObjectURL(downloadData)
    save_link.download = name
    this.fake_click(save_link)
  }

  fake_click(obj) {
    const ev = document.createEvent("MouseEvents")
    ev.initMouseEvent(
      "click",
      true,
      false,
      window,
      0,
      0,
      0,
      0,
      0,
      false,
      false,
      false,
      false,
      0,
      null,
    )
    obj.dispatchEvent(ev)
  }

  private async _historyMkdir(keys: string[]) {
    const his_mkdir = []
    const donedir = []
    for (let index = 0; index < keys.length; index++) {
      const key = keys[index]
      const keySplit = key.split("/")

      if (keySplit.length > 4) {
        if (keySplit[3] === "RetouchHistory") {
          if (!this.isnewhismkdirdone) {
            this.isnewhismkdirdone = true
            his_mkdir.push(`/test/case/RetouchHistory`)
          }

          if (keySplit.length > 5) {
            let donekey = false
            for (let i = 0; i < donedir.length; i++) {
              if (donedir[i] === keySplit[4]) {
                donekey = true
                break
              }
            }
            if (!donekey) {
              donedir.push(keySplit[4])

              const baseDir = `/test/case/RetouchHistory/` + keySplit[4]
              his_mkdir.push(baseDir)

              his_mkdir.push(baseDir + "/PonticLib")
              his_mkdir.push(baseDir + "/PonticLib/MirrorToothLib")
              his_mkdir.push(baseDir + "/PonticLib/StandardToothLib")

              his_mkdir.push(baseDir + "/Setting Data2")
              his_mkdir.push(baseDir + "/Setting Data2/PonticLib")
              his_mkdir.push(
                baseDir + "/Setting Data2/PonticLib/MirrorToothLib",
              )
              his_mkdir.push(
                baseDir + "/Setting Data2/PonticLib/StandardToothLib",
              )
              his_mkdir.push(baseDir + "/Setting Data2/biteRampData")
              his_mkdir.push(baseDir + "/Setting Data2/biteRampData/InfoData0")
              his_mkdir.push(baseDir + "/Setting Data2/biteRampData/InfoData1")
              his_mkdir.push(baseDir + "/biteRampData")
              his_mkdir.push(baseDir + "/biteRampData/InfoData0")
              his_mkdir.push(baseDir + "/biteRampData/InfoData1")
              his_mkdir.push(baseDir + "/Setting Data0")
              his_mkdir.push(baseDir + "/Setting Data1")
              his_mkdir.push(baseDir + "/Setting Data10")
              his_mkdir.push(baseDir + "/Raw")
            }
          }
        }
      }
      if (keySplit.length > 5) {
        if (keySplit[4] === "RetouchHistory") {
          if (!this.isoldhismkdirdone) {
            this.isoldhismkdirdone = true
            his_mkdir.push(`/test/case/Setting Data10/RetouchHistory`)
          }

          if (keySplit.length > 6) {
            let donekey = false
            for (let i = 0; i < donedir.length; i++) {
              if (donedir[i] === keySplit[5]) {
                donekey = true
                break
              }
            }
            if (!donekey) {
              donedir.push(keySplit[5])

              const baseDir = `/test/case/Setting Data10/RetouchHistory`
              his_mkdir.push(baseDir + "/" + keySplit[5])
              his_mkdir.push(baseDir + "/" + keySplit[5] + "/PonticLib")
              his_mkdir.push(
                baseDir + "/" + keySplit[5] + "/PonticLib/MirrorToothLib",
              )
              his_mkdir.push(
                baseDir + "/" + keySplit[5] + "/PonticLib/StandardToothLib",
              )
              his_mkdir.push(baseDir + "/" + keySplit[5] + "/Setting Data0")
              his_mkdir.push(baseDir + "/" + keySplit[5] + "/Setting Data1")
              his_mkdir.push(baseDir + "/" + keySplit[5] + "/Setting Data10")
              his_mkdir.push(baseDir + "/" + keySplit[5] + "/biteRampData")
              his_mkdir.push(
                baseDir + "/" + keySplit[5] + "/biteRampData/InfoData0",
              )
              his_mkdir.push(
                baseDir + "/" + keySplit[5] + "/biteRampData/InfoData1",
              )
            }
          }
        }

        if (keySplit[3] === "RetouchHistory") {
          if (!this.isnewhismkdirdone) {
            this.isnewhismkdirdone = true
            his_mkdir.push(`/test/case/RetouchHistory`)
          }

          if (keySplit.length > 6) {
            let donekey = false
            for (let i = 0; i < donedir.length; i++) {
              if (donedir[i] === keySplit[4]) {
                donekey = true
                break
              }
            }
            if (!donekey) {
              donedir.push(keySplit[4])

              const baseDir = `/test/case/RetouchHistory/` + keySplit[4]
              his_mkdir.push(baseDir)

              his_mkdir.push(baseDir + "/PonticLib")
              his_mkdir.push(baseDir + "/PonticLib/MirrorToothLib")
              his_mkdir.push(baseDir + "/PonticLib/StandardToothLib")

              his_mkdir.push(baseDir + "/Setting Data2")
              his_mkdir.push(baseDir + "/Setting Data2/PonticLib")
              his_mkdir.push(
                baseDir + "/Setting Data2/PonticLib/MirrorToothLib",
              )
              his_mkdir.push(
                baseDir + "/Setting Data2/PonticLib/StandardToothLib",
              )
              his_mkdir.push(baseDir + "/Setting Data2/biteRampData")
              his_mkdir.push(baseDir + "/Setting Data2/biteRampData/InfoData0")
              his_mkdir.push(baseDir + "/Setting Data2/biteRampData/InfoData1")
              his_mkdir.push(baseDir + "/biteRampData")
              his_mkdir.push(baseDir + "/biteRampData/InfoData0")
              his_mkdir.push(baseDir + "/biteRampData/InfoData1")
              his_mkdir.push(baseDir + "/Setting Data0")
              his_mkdir.push(baseDir + "/Setting Data1")
              his_mkdir.push(baseDir + "/Setting Data10")
              his_mkdir.push(baseDir + "/Raw")
            }
          }
        }
      }
    }
    try {
      for (let i = 0; i < his_mkdir.length; i++) {
        console.log("_historyMkdir his_mkdir:", his_mkdir[i])
        await wasmModule.module.FS.mkdir(his_mkdir[i])
      }
    } catch (excp) {
      throw excp
    }
  }

  private async _deleteFolderRecursive(folderPath: string) {
    if (wasmModule.module.FS.analyzePath(folderPath).exists) {
      const files = wasmModule.module.FS.readdir(folderPath)
      for (let i = 0; i < files.length; i++) {
        if (files[i] === "." || files[i] === "..") continue
        const filePath = folderPath + "/" + files[i]
        if (wasmModule.module.FS.analyzePath(filePath).exists) {
          const filePathStats = wasmModule.module.FS.stat(filePath)
          if (wasmModule.module.FS.isDir(filePathStats.mode)) {
            await this._deleteFolderRecursive(filePath)
            await wasmModule.module.FS.rmdir(filePath)
          } else {
            await wasmModule.module.FS.unlink(filePath)
          }
        }
      }
      console.log(`Folder ${folderPath} and its contents deleted successfully`)
    }
  }
  async _mkDirByKeys(keys: string[]) {
    const dirs = new Set()
    for (let index = 0; index < keys.length; index++) {
      const key = keys[index]
      const splits = key.split("/")
      let dir = ""
      for (let j = 1; j < splits.length - 1; j++) {
        dir = dir + "/" + splits[j]
        dirs.add(dir)
      }
    }
    for (const dir of dirs.keys()) {
      console.log("Making dir: ", dir)
      try {
        await wasmModule.module.FS.mkdir(dir)
      } catch (excp) {
        console.warn("Make dir failed:", dir, excp)
      }
    }
  }
}
export const resourcesSynchronization = new ResourcesSynchronization()
